import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import copy from "copy-to-clipboard";
import { Bot, MessageSquarePlus, Send } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ReactComponent as CopyIcon } from "../../../../assets/Images/CopyIcon.svg";
import { CopyAlert } from "../../CommonComponents/CommonComponents";
import { fetchSessions } from "./logBotApi";
import type { ChatMessage, SessionSummary, ToolCall } from "./logBotTypes";
import { useLogBotWebSocket } from "./useLogBotWebSocket";

const TOOL_LABELS: Record<string, string> = {
  get_call_logs: "Reading logs",
  get_call_events: "Reading events",
  get_call_prompt: "Reading prompt",
  get_call_transcript: "Reading transcript",
};

const toolLabel = (name: string) =>
  TOOL_LABELS[name] || name.replace(/_/g, " ");

const markdownComponents = {
  a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    const safeHref =
      href && /^(https?:|mailto:)/i.test(href) ? href : undefined;
    return (
      <a
        {...props}
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  },
};

interface Props {
  callUuid: string;
  phone?: string;
  open: boolean;
  onClose: () => void;
}

const LogBotPanel: React.FC<Props> = ({ callUuid, phone, open, onClose }) => {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [resumeSessionId, setResumeSessionId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [size, setSize] = useState({ width: 720, height: 560 });
  const [copyAlert, setCopyAlert] = useState({ open: false, message: "" });
  const panelRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef<{
    startX: number;
    startY: number;
    startW: number;
    startH: number;
  } | null>(null);

  const handleAuthFailed = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key !== "pay-redirection") localStorage.removeItem(key);
    });
    if (window?.location?.pathname !== "/") window.location.href = "/";
  };

  const {
    status,
    messages,
    callBrief,
    activeTools,
    error,
    activeSessionId,
    isStreaming,
    sendMessage,
    clearError,
    startNewSession,
  } = useLogBotWebSocket({
    callUuid,
    sessionId: resumeSessionId,
    enabled: open && Boolean(callUuid),
    onAuthFailed: handleAuthFailed,
    onSessionReady: () => {
      loadSessions();
    },
  });

  const loadSessions = async () => {
    if (!callUuid) return;
    setSessionsLoading(true);
    try {
      const list = await fetchSessions(callUuid);
      setSessions(list);
    } catch (err) {
      console.log(err);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, callUuid]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeTools, isStreaming]);

  // Drive panel size via CSS variables (no inline style / sx)
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    el.style.setProperty("--log-bot-w", `${size.width}px`);
    el.style.setProperty("--log-bot-h", `${size.height}px`);
  }, [size]);

  const handleSend = () => {
    if (!input.trim() || status !== "ready" || isStreaming) return;
    sendMessage(input);
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = (text: string) => {
    copy(text);
    setCopyAlert({ open: true, message: "Response copied!" });
  };

  const handleResume = (sid: string) => {
    if (sid === activeSessionId) return;
    setResumeSessionId(sid);
  };

  const handleNewChat = () => {
    if (resumeSessionId) {
      setResumeSessionId(null);
    } else {
      startNewSession();
    }
  };

  const onResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    resizingRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: size.width,
      startH: size.height,
    };

    const onMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const dx = ev.clientX - resizingRef.current.startX;
      const dy = ev.clientY - resizingRef.current.startY;
      setSize({
        width: Math.min(
          Math.max(resizingRef.current.startW + dx, 420),
          window.innerWidth - 40
        ),
        height: Math.min(
          Math.max(resizingRef.current.startH + dy, 360),
          window.innerHeight - 40
        ),
      });
    };

    const onUp = () => {
      resizingRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  if (!open) return null;

  const statusLabel =
    status === "connecting"
      ? "Connecting…"
      : status === "ready"
      ? "Online"
      : status === "error"
      ? "Error"
      : "Offline";

  const statusColor =
    status === "ready" ? "success" : status === "error" ? "error" : "default";

  return (
    <Box ref={panelRef} className="log-bot-panel">
      <Box className="log-bot-header">
        <Box className="log-bot-header-main">
          <Bot size={18} className="log-bot-header-icon" />
          <Box>
            <Typography className="font-14-600-grey">
              Call Log Bot
              {phone ? (
                <Typography component="span" className="font-12-400-lightgrey">
                  {" "}
                  · {phone}
                </Typography>
              ) : null}
            </Typography>
            <Typography className="font-11-500-blue log-bot-header-meta">
              {callBrief?.campaign_name || callUuid}
              {callBrief?.duration != null ? ` · ${callBrief.duration}s` : ""}
              {callBrief?.pbx_status_text
                ? ` · ${callBrief.pbx_status_text}`
                : ""}
            </Typography>
          </Box>
        </Box>
        <Box className="log-bot-header-actions">
          <Chip
            size="small"
            label={statusLabel}
            color={statusColor}
            className="log-bot-status-chip"
          />
          <Tooltip title="New conversation">
            <IconButton size="small" onClick={handleNewChat}>
              <MessageSquarePlus size={16} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box className="log-bot-body">
        <Box className="log-bot-sessions">
          <Typography className="font-12-600-grey log-bot-sessions-title">
            Past conversations
          </Typography>
          {sessionsLoading ? (
            <Box className="log-bot-sessions-loading">
              <CircularProgress size={18} />
            </Box>
          ) : sessions.length === 0 ? (
            <Typography className="font-11-400-lightgrey log-bot-sessions-empty">
              No past chats yet
            </Typography>
          ) : (
            sessions.map((s) => (
              <Box
                key={s.session_id}
                className={`log-bot-session-item${
                  s.session_id === activeSessionId ? " active" : ""
                }`}
                onClick={() => handleResume(s.session_id)}
              >
                <Typography className="font-12-500-grey" noWrap>
                  {s.user || "Session"}
                </Typography>
                <Typography className="font-11-400-lightgrey" noWrap>
                  {s.message_count} msgs ·{" "}
                  {s.last_active
                    ? new Date(s.last_active).toLocaleString()
                    : ""}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        <Box className="log-bot-chat">
          <Box className="log-bot-messages">
            {status === "connecting" && messages.length === 0 && (
              <Box className="log-bot-messages-loading">
                <CircularProgress size={22} />
              </Box>
            )}

            {messages.length === 0 && status === "ready" && (
              <Box className="log-bot-empty">
                <Bot size={28} className="log-bot-empty-icon" />
                <Typography className="font-13-500-grey">
                  Ask anything about this call
                </Typography>
                <Typography className="font-11-400-lightgrey">
                  e.g. What was the prompt? Why was there a gap?
                </Typography>
              </Box>
            )}

            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                onCopy={() => handleCopy(msg.content)}
              />
            ))}

            {activeTools.length > 0 && (
              <Box className="log-bot-tools">
                {activeTools.map((t) => (
                  <ToolChip key={t.name} tool={t} />
                ))}
              </Box>
            )}

            {error && (
              <Box className="log-bot-error" onClick={clearError}>
                <Typography className="font-12-400-lightgrey">{error}</Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Box className="log-bot-input-row">
            <TextField
              className="log-bot-input"
              fullWidth
              size="small"
              multiline
              maxRows={4}
              placeholder={
                status === "ready"
                  ? "Ask about this call…"
                  : "Waiting for connection…"
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={status !== "ready" || isStreaming}
            />
            <IconButton
              className="log-bot-send-btn"
              onClick={handleSend}
              disabled={!input.trim() || status !== "ready" || isStreaming}
            >
              <Send size={16} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box className="log-bot-resize" onMouseDown={onResizeMouseDown} />

      <CopyAlert
        open={copyAlert.open}
        onClose={() => setCopyAlert({ ...copyAlert, open: false })}
        alertMessage={copyAlert.message}
      />
    </Box>
  );
};

const ToolChip: React.FC<{ tool: ToolCall }> = ({ tool }) => {
  const running = tool.status === "running";
  const errored = tool.status === "error";
  return (
    <Chip
      size="small"
      className="log-bot-tool-chip"
      icon={running ? <CircularProgress size={10} /> : undefined}
      label={`${toolLabel(tool.name)}${running ? "…" : ""}`}
      color={errored ? "error" : running ? "primary" : "default"}
      variant={running ? "filled" : "outlined"}
    />
  );
};

const MessageBubble: React.FC<{
  message: ChatMessage;
  onCopy: () => void;
}> = ({ message, onCopy }) => {
  const isUser = message.role === "user";

  return (
    <Box className={`log-bot-bubble-row ${isUser ? "user" : "assistant"}`}>
      <Box className={`log-bot-bubble ${isUser ? "user" : "assistant"}`}>
        <Typography className="font-11-500-blue log-bot-bubble-role">
          {isUser ? "You" : "Bot"}
          {message.isStreaming ? " · typing…" : ""}
        </Typography>
        {isUser ? (
          <Typography className="font-13-400-grey log-bot-bubble-text">
            {message.content}
          </Typography>
        ) : (
          <Box className="log-bot-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {message.content || (message.isStreaming ? "▍" : "")}
            </ReactMarkdown>
          </Box>
        )}
        {!isUser && message.content && !message.isStreaming && (
          <Box className="log-bot-bubble-actions">
            <Tooltip title="Copy response">
              <IconButton size="small" onClick={onCopy}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LogBotPanel;
